import React, { useState, useRef } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Alert,
  Badge,
  Row,
  Col,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Table
} from 'reactstrap';
import SubidaFacturaFilePond from './SubidaFacturaFilePond';
import ModalMedicamentosFactura from './ModalMedicamentosFactura';
import { extraerDatosFacturaIA } from '../../helpers/backend_helper';
import { toast } from 'react-toastify';
import { toInputDate, toBackendDate } from '../../utils/dateUtils';

/**
 * Componente para validar imágenes de facturas antes de enviar al backend
 * 
 * Validaciones:
 * - Tamaño mínimo: 800x600px (0.48MP) - solo para imágenes
 * - Tamaño máximo: 10MB
 * - Formatos: JPG, PNG, PDF
 * 
 * @param {Function} onImagenValida - Callback cuando imagen es válida (file, metricas)
 * @param {Function} onImagenInvalida - Callback cuando imagen es inválida (errores)
 * @param {Function} onValidacionBackend - Callback con resultado de extracción IA
 * @param {Number} maxSizeMB - Tamaño máximo en MB (default: 10)
 * @param {Number} minWidth - Ancho mínimo en px (default: 800)
 * @param {Number} minHeight - Alto mínimo en px (default: 600)
 * @param {Number} minMegapixels - Megapixels mínimos (default: 0.48)
 */
const ValidadorImagenFactura = ({
  onImagenValida,
  onImagenInvalida,
  onValidacionBackend,
  maxSizeMB = 10,
  minWidth = 800,
  minHeight = 600,
  minMegapixels = 0.48
}) => {
  const [respuestaBackend, setRespuestaBackend] = useState(null);
  const [errorBackend, setErrorBackend] = useState(null);
  const [imagenCorregida, setImagenCorregida] = useState(null);
  const [imagenOriginal, setImagenOriginal] = useState(null);
  const [extrayendo, setExtrayendo] = useState(false);
  const [archivoOriginal, setArchivoOriginal] = useState(null);
  const [errorExtraccion, setErrorExtraccion] = useState(null);
  const [filePondKey, setFilePondKey] = useState(0); // Key para resetear FilePond
  
  // Estados para modal de confirmación
  const [modalAbierto, setModalAbierto] = useState(false);
  const [datosExtraidos, setDatosExtraidos] = useState(null);
  const [fechaEditable, setFechaEditable] = useState('');
  const [numeroEditable, setNumeroEditable] = useState('');
  const [montoEditable, setMontoEditable] = useState('');
  const [metricas, setMetricas] = useState(null);
  
  // useRef para evitar problema de closure en setTimeout
  const archivoRef = useRef(null);

  /**
   * Handler cuando FilePond completa la validación
   */
  const handleValidacionCompleta = (resultado, archivo) => {
    setErrorExtraccion(null);
    setDatosExtraidos(null);
    
    setArchivoOriginal(archivo); 
    archivoRef.current = archivo; 
    
    setMetricas(resultado.metricas);
    
    const debeExtraer = resultado.valida && resultado.errores && resultado.errores.length === 0;
    
    if (debeExtraer) {
      setTimeout(() => {
        handleContinuar();
      }, 1000);
    }
  };

  /**
   * Handler cuando hay error en FilePond
   */
  const handleError = (error) => {
    setErrorBackend(error.message || 'Error al validar imagen');
  };

  /**
   * Limpiar estado
   */
  const limpiarImagen = () => {
    setRespuestaBackend(null);
    setErrorBackend(null);
    setImagenCorregida(null);
    setImagenOriginal(null);
  };

  /**
   * Handler para confirmar datos del modal (medicamentos editados)
   * Pre-llena el formulario de facturación con los datos validados
   */
  const handleConfirmarDatos = (medicamentosEditados) => {
    if (!datosExtraidos || !onValidacionBackend) return;

    let fechaFormateada = fechaEditable;
    if (fechaEditable) {
      const [año, mes, dia] = fechaEditable.split('-');
      const fechaConBarras = `${dia}/${mes}/${año}`;
      fechaFormateada = toBackendDate(fechaConBarras);
    }


    const datosActualizados = {
      ...datosExtraidos,
      datos: {
        ...datosExtraidos.datos,
        fecha: fechaFormateada, 
        numero_factura: numeroEditable,
        monto_total: parseFloat(montoEditable.replace(',', '.')) || 0,
        total_bolivares: parseFloat(montoEditable.replace(',', '.')) || 0,
        medicamentos: medicamentosEditados
      },
      confirmado: true
    };

    onValidacionBackend(datosActualizados);

    setModalAbierto(false);
    
    toast.success('Datos confirmados. Revisa y haz clic en "Actualizar Facturación" para guardar.', { autoClose: 4000 });
  };

  /**
   * Handler para cancelar modal
   */
  const handleCancelarModal = () => {
    setModalAbierto(false);
    setDatosExtraidos(null);
  };

  /**
   * Handler cuando usuario confirma usar esta imagen
   * Llama a /extraer-datos para obtener datos estructurados
   */
  const handleContinuar = async () => {
    const archivo = archivoRef.current || archivoOriginal;
    
    if (!archivo) {
      console.error('❌ No hay archivo para procesar');
      setErrorExtraccion('No hay archivo seleccionado');
      return;
    }

    setExtrayendo(true);

    try {
      
      const fileName = archivo.name || 'factura.jpg';
      const fileExtension = fileName.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];
      
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        throw new Error(`Extensión de archivo no válida: ${fileExtension}. Permitidas: ${allowedExtensions.join(', ')}`);
      }
      
      const formData = new FormData();
      
      let mimeType = archivo.type;
      if (!mimeType || mimeType === 'application/octet-stream') {
        const mimeTypes = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'pdf': 'application/pdf'
        };
        mimeType = mimeTypes[fileExtension] || 'image/jpeg';
      }
      
      const fileToSend = new File([archivo], fileName, { type: mimeType });
      formData.append('file', fileToSend);

      const resultado = await extraerDatosFacturaIA(formData);
      
      if (resultado.success) {
        
        setDatosExtraidos({
          ...resultado,
          imagenParaSubir: archivo,
          validacion: respuestaBackend
        });
        
        let fechaConvertida = '';
        if (resultado.datos.fecha) {
          const fechaConBarras = resultado.datos.fecha.replace(/-/g, '/');
          fechaConvertida = toInputDate(fechaConBarras);
        }
        setFechaEditable(fechaConvertida);
        
        setNumeroEditable(resultado.datos.numero_factura || '');
        
        let montoFormateado = '';
        if (resultado.datos.monto_total || resultado.datos.total_bolivares) {
          const monto = resultado.datos.monto_total || resultado.datos.total_bolivares;
          montoFormateado = monto.toString().replace('.', ',');
        }
        setMontoEditable(montoFormateado);
        
        onValidacionBackend({
          ...resultado,
          imagenParaSubir: archivo,
          validacion: respuestaBackend,
          confirmado: true // Marcar como confirmado automáticamente
        });
        
        toast.success(
          `Datos extraídos: ${resultado.datos.medicamentos?.length || 0} medicamentos. Revisa en el formulario.`,
          { autoClose: 3000 }
        );
      } else {
        console.error('❌ resultado.success es false');
        throw new Error(resultado.error || 'Error extrayendo datos');
      }
    } catch (error) {
      console.error('❌ Error extrayendo datos:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error data:', error.response?.data);
      console.error('❌ Error data type:', typeof error.response?.data);
      console.error('❌ Error data detail:', error.response?.data?.detail);
      console.error('❌ Error data detail type:', typeof error.response?.data?.detail);
      
      // Determinar mensaje de error apropiado
      let mensajeError = 'Error al procesar la imagen. Por favor, intente con otra imagen o ingrese los datos manualmente.';
      let tipoError = 'error';
      
      // Errores específicos según código HTTP
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        // Primero intentar extraer mensaje específico del backend
        let mensajeBackend = null;
        if (data) {
          if (typeof data === 'string') {
            mensajeBackend = data;
          } else if (data.detail) {
            if (typeof data.detail === 'string') {
              mensajeBackend = data.detail;
            } else if (data.detail.mensaje) {
              mensajeBackend = data.detail.mensaje;
            } else if (data.detail.error) {
              mensajeBackend = data.detail.error;
            }
          } else if (data.error) {
            mensajeBackend = data.error;
          } else if (data.message) {
            mensajeBackend = data.message;
          }
        }
        
        // Detectar si es error de imagen pequeña
        if (mensajeBackend && (mensajeBackend.toLowerCase().includes('imagen pequeña') || mensajeBackend.toLowerCase().includes('imagen muy pequeña'))) {
          // NO mostrar mensaje largo aquí, pasar al padre que tiene la UI mejorada
          // Toast corto
          toast.error('⚠️ Imagen de baja calidad. Ver detalles debajo.', { autoClose: 4000 });
          
          // Pasar error al padre (Facturacion.js) que mostrará la UI mejorada
          if (onValidacionBackend) {
            const archivo = archivoRef.current || archivoOriginal;
            onValidacionBackend({
              success: false,
              confirmado: true,
              error: mensajeBackend, // Pasar mensaje del backend con dimensiones
              imagenParaSubir: archivo,
              validacion: respuestaBackend
            });
          }
          
          setExtrayendo(false);
          return;
        }
        
        // Si el backend no envió mensaje claro, usar mensajes por defecto según código HTTP
        // Detectar mensajes genéricos del backend que no son útiles
        const mensajesGenericos = [
          'Error en la respuesta del servidor',
          'Unprocessable Entity',
          'Bad Request',
          'Internal Server Error'
        ];
        const esGenerico = mensajeBackend && mensajesGenericos.some(msg => 
          mensajeBackend.includes(msg)
        );
        
        if (mensajeBackend && !esGenerico) {
          // Backend envió mensaje específico y útil
          mensajeError = mensajeBackend;
          // Determinar tipo según código
          if (status === 422 || status === 400 || status === 413) {
            tipoError = 'warning';
          }
        } else {
          // Mensajes por defecto según código HTTP
          if (status === 422) {
            // Error de validación - probablemente no es una factura
            mensajeError = '⚠️ Esta imagen no parece ser una factura válida. Por favor, suba una imagen de factura o ingrese los datos manualmente.';
            tipoError = 'warning';
          } else if (status === 400) {
            // Bad request
            mensajeError = '⚠️ La imagen no pudo ser procesada. Verifique que sea una factura clara y legible.';
            tipoError = 'warning';
          } else if (status === 413) {
            // Archivo muy grande
            mensajeError = '⚠️ La imagen es muy grande. Por favor, use una imagen más pequeña.';
            tipoError = 'warning';
          } else if (status === 500) {
            // Error del servidor
            mensajeError = '❌ Error del servidor al procesar la imagen. Por favor, intente nuevamente o ingrese los datos manualmente.';
          }
        }
      } else if (error.message) {
        mensajeError = `Error: ${error.message}`;
      }
      
      // Mostrar toast según tipo de error
      if (tipoError === 'warning') {
        toast.warning(mensajeError, { autoClose: 7000 });
      } else {
        toast.error(mensajeError, { autoClose: 7000 });
      }
      
      // Guardar error para mostrar en UI
      setErrorExtraccion({
        mensaje: mensajeError,
        tipo: tipoError,
        status: error.response?.status
      });
      
      // NO llamar callback - dejar que usuario decida qué hacer
      // Puede intentar con otra imagen o cambiar a ingreso manual
    } finally {
      setExtrayendo(false);
    }
  };


  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">
          <i className="ri-image-line me-2"></i>
          Validación de Imagen de Factura
        </h5>
      </CardHeader>
      <CardBody>
        {/* FilePond para subida de imagen */}
        <SubidaFacturaFilePond 
          key={filePondKey}
          onValidacionCompleta={handleValidacionCompleta}
          onError={handleError}
          extrayendoDatos={extrayendo}
        />

        {/* Error del backend */}
        {errorBackend && (
          <Alert color="danger" className="mt-3 mb-0">
            <h6 className="alert-heading fs-14 mb-2">
              <i className="ri-error-warning-line me-1"></i>
              Error del Servidor
            </h6>
            <p className="mb-0 fs-13">{errorBackend}</p>
          </Alert>
        )}

        {/* Error de extracción */}
        {errorExtraccion && (
          <Alert color={errorExtraccion.tipo === 'warning' ? 'warning' : 'danger'} className="mt-3">
            <h6 className="alert-heading fs-14 mb-2">
              <i className={`${errorExtraccion.tipo === 'warning' ? 'ri-alert-line' : 'ri-error-warning-line'} me-1`}></i>
              {errorExtraccion.tipo === 'warning' ? 'Advertencia' : 'Error al Procesar Imagen'}
            </h6>
            <p className="mb-2 fs-13">{errorExtraccion.mensaje}</p>
            
            {/* Sugerencias para mejorar la imagen */}
            <div className="mt-3 p-2 bg-light rounded">
              <p className="mb-2 fw-medium fs-13">
                <i className="ri-lightbulb-line me-1"></i>
                Sugerencias para mejorar la extracción:
              </p>
              <ul className="mb-0 fs-12">
                <li>Tome la foto más cerca (mínimo 800x800px)</li>
                <li>Asegure buena iluminación sin sombras</li>
                <li>Evite reflejos y texto borroso</li>
                <li>Prefiera facturas impresas sobre tickets térmicos</li>
              </ul>
            </div>
            
            <div className="d-flex gap-2 mt-3">
              <Button 
                color="primary" 
                size="sm"
                onClick={() => {
                  // Limpiar estados
                  setErrorExtraccion(null);
                  setRespuestaBackend(null);
                  setErrorBackend(null);
                  setImagenCorregida(null);
                  setImagenOriginal(null);
                  setArchivoOriginal(null);
                  archivoRef.current = null;
                  // Resetear FilePond cambiando la key
                  setFilePondKey(prev => prev + 1);
                }}
              >
                <i className="ri-refresh-line me-1"></i>
                Intentar con Otra Imagen
              </Button>
              <Button 
                color="secondary" 
                size="sm"
                onClick={() => {
                  // Cambiar a modo manual
                  if (onValidacionBackend) {
                    const archivo = archivoRef.current || archivoOriginal;
                    onValidacionBackend({
                      success: false,
                      confirmado: true,
                      error: errorExtraccion.mensaje,
                      imagenParaSubir: archivo, // ⭐ Pasar imagen para preview
                      validacion: respuestaBackend // Incluir validación de calidad
                    });
                  }
                }}
              >
                <i className="ri-edit-line me-1"></i>
                Ingresar Datos Manualmente
              </Button>
            </div>
          </Alert>
        )}

        {/* Respuesta del backend */}
        {respuestaBackend && (
          <>
            {/* Comparación de imágenes si hay corrección */}
            {imagenCorregida && (
              <div className="mt-4">
                <h6 className="fs-14 mb-3">
                  <i className="ri-image-2-line me-1"></i>
                  Comparación de Imágenes
                </h6>
                <Row>
                  <Col md={6}>
                    <div className="text-center">
                      <Badge color="secondary" className="mb-2">Imagen Original</Badge>
                      <div className="border rounded p-2">
                        <p className="text-muted fs-12 mb-0">
                          La imagen corregida se usará para la extracción de datos
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="text-center">
                      <Badge color="success" className="mb-2">
                        <i className="ri-refresh-line me-1"></i>
                        Imagen Corregida
                      </Badge>
                      <img 
                        src={imagenCorregida} 
                        alt="Corregida" 
                        className="img-fluid rounded border border-success"
                        style={{ maxHeight: '300px', objectFit: 'contain' }}
                      />
                      {respuestaBackend?.angulo_rotacion && respuestaBackend.angulo_rotacion !== 0 && (
                        <Badge color="success" className="mt-2 d-block">
                          Rotación corregida: {respuestaBackend.angulo_rotacion}°
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            )}

            {/* Solo mostrar información si hay errores */}
            {!respuestaBackend.valida && (
              <Alert color="danger" className="mt-3 mb-0">
                <h6 className="alert-heading fs-14 mb-2">
                  <i className="ri-close-circle-line me-1"></i>
                  Validación Fallida
                </h6>
                <div className="fs-13">
                  <p className="mb-2">La imagen no pasó la validación.</p>
                  {respuestaBackend.errores?.length > 0 && (
                    <ul className="mb-0 ps-3">
                      {respuestaBackend.errores.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </Alert>
            )}

            {/* Botón de continuar (Solo visible si NO se está extrayendo automáticamente, para evitar duplicidad con el loader de cerebro) */}
            {respuestaBackend?.valida && !extrayendo && (
              <div className="text-center mt-4">
                <Button 
                  color="success" 
                  size="lg"
                  onClick={handleContinuar}
                >
                  <i className="ri-check-line me-1"></i>
                  Continuar con esta Imagen
                </Button>
              </div>
            )}
          </>
        )}
      </CardBody>

      {/* Modal eliminado - Los datos se muestran directamente en el formulario principal */}
    </Card>
  );
};

export default ValidadorImagenFactura;
